import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Form, Input, Select, Switch, Button, Space, Spin, Alert, message } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  getEmailTemplate,
  createEmailTemplate,
  updateEmailTemplate,
  type CreateEmailTemplateRequest,
  type UpdateEmailTemplateRequest,
} from '@/api/adminApi';
import { useAuth } from '@/hooks/useAuth';

const TEMPLATE_TYPES = ['welcome', 'reset_password', 'invoice', 'notification', 'ticket_update', 'campaign'];

interface TemplateFormValues {
  name: string;
  type: string;
  subject: string;
  body_html: string;
  body_text: string;
  description: string;
  is_active: boolean;
  variables_text: string;
}

export default function EmailTemplateForm() {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [form] = Form.useForm<TemplateFormValues>();

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isEdit || !id) return;
    setLoading(true);
    setError(null);
    getEmailTemplate(id)
      .then((tmpl) => {
        form.setFieldsValue({
          name: tmpl.name,
          type: tmpl.type,
          subject: tmpl.subject,
          body_html: tmpl.body_html,
          body_text: tmpl.body_text || '',
          description: tmpl.description || '',
          is_active: tmpl.is_active,
          variables_text: tmpl.variables ? tmpl.variables.join(', ') : '',
        });
      })
      .catch((err) => setError(err instanceof Error ? err.message : t('app.not_found')))
      .finally(() => setLoading(false));
  }, [isEdit, id, form, t]);

  const handleSubmit = async (values: TemplateFormValues) => {
    setSubmitting(true);
    setError(null);
    try {
      const variables = values.variables_text
        ? values.variables_text.split(',').map((v) => v.trim()).filter(Boolean)
        : [];

      if (isEdit && id) {
        const dto: UpdateEmailTemplateRequest = {
          name: values.name,
          subject: values.subject,
          body_html: values.body_html,
          body_text: values.body_text || null,
          description: values.description || null,
          is_active: values.is_active,
          variables,
        };
        await updateEmailTemplate(id, dto);
        message.success(t('admin.email_template_update_success'));
      } else {
        const dto: CreateEmailTemplateRequest = {
          name: values.name,
          type: values.type,
          subject: values.subject,
          body_html: values.body_html,
          body_text: values.body_text || undefined,
          description: values.description || undefined,
          is_active: values.is_active,
          variables,
        };
        await createEmailTemplate(dto);
        message.success(t('admin.email_template_create_success'));
      }
      navigate('/admin/email-templates');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('app.error'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!hasPermission('admin', 'write')) {
    return <Alert type="error" message={t('app.forbidden')} />;
  }

  if (loading) return <Spin size="large" />;

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/admin/email-templates')}>
          {t('app.back')}
        </Button>
      </Space>

      <h2>{isEdit ? t('admin.edit_email_template_title') : t('admin.create_email_template_title')}</h2>

      {error && <Alert type="error" message={error} style={{ marginBottom: 16 }} />}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{ type: 'notification', is_active: true }}
        style={{ maxWidth: 800 }}
      >
        <Form.Item
          name="name"
          label={t('admin.email_template_field.name')}
          rules={[{ required: true, message: t('admin.validation.name_required') }]}
        >
          <Input placeholder={t('admin.email_template_field.name')} />
        </Form.Item>

        <Form.Item
          name="type"
          label={t('admin.email_template_field.type')}
          rules={[{ required: true }]}
        >
          <Select disabled={isEdit}>
            {TEMPLATE_TYPES.map((tp) => (
              <Select.Option key={tp} value={tp}>
                {t(`admin.email_template_type.${tp}`)}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="subject"
          label={t('admin.email_template_field.subject')}
          rules={[{ required: true, message: t('admin.validation.subject_required') }]}
        >
          <Input placeholder={t('admin.email_template_field.subject')} />
        </Form.Item>

        <Form.Item name="description" label={t('admin.email_template_field.description')}>
          <Input.TextArea rows={2} placeholder={t('admin.email_template_field.description')} />
        </Form.Item>

        <Form.Item
          name="body_html"
          label={t('admin.email_template_field.body_html')}
          rules={[{ required: true, message: t('admin.validation.body_html_required') }]}
        >
          <Input.TextArea rows={10} placeholder="<html>...</html>" />
        </Form.Item>

        <Form.Item name="body_text" label={t('admin.email_template_field.body_text')}>
          <Input.TextArea rows={5} placeholder={t('admin.email_template_field.body_text')} />
        </Form.Item>

        <Form.Item name="variables_text" label={t('admin.email_template_field.variables')}>
          <Input placeholder="name, email, company" />
        </Form.Item>

        <Form.Item name="is_active" label={t('admin.email_template_field.is_active')} valuePropName="checked">
          <Switch />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={submitting}>
              {t('app.save')}
            </Button>
            <Button onClick={() => navigate('/admin/email-templates')}>{t('app.cancel')}</Button>
          </Space>
        </Form.Item>
      </Form>
    </div>
  );
}
